/**
 * Does this error mean the session is over?
 *
 * Pure and separate from the Apollo client so it can be tested without
 * standing up a transport — and because getting it wrong in either direction
 * is expensive.
 *
 * THE SHAPE OF THE PROBLEM: an expired or revoked token does not arrive as an
 * HTTP 401. The backend answers 200 with a GraphQL error whose message reads
 * "403: Authentication required", so anything watching status codes alone
 * never sees it.
 *
 * MATCHING IS NARROW ON PURPOSE. These messages mean the caller has no usable
 * identity. A permission refusal does not: "endpoint not available in this
 * environment" is a gate, a staff-only resolver answers empty rather than
 * erroring, and a business rule that refuses an action is not a dead session.
 * Throwing somebody out mid-task for asking the wrong question would be worse
 * than the bug this fixes, so anything unrecognised is left alone.
 */

/** No identity at all — no token, or one the server would not accept. */
const NO_IDENTITY = /^\s*40[13]\s*:\s*(authentication required|authentication token is missing|not authenticated)/i;

/**
 * A token the server refused to decode — expired, tampered with, or signed by
 * a key that has since rotated. `requireAuth` wraps the underlying reason into
 * a 401, so the text varies and only the 401 plus a token-ish word is reliable.
 */
const BAD_TOKEN = /^\s*401\s*:.*(expired|invalid token|signature|decode|credential)/i;

/** Gates and refusals that share the 403 prefix but are NOT auth failures. */
const NOT_AUTH = /endpoint not available|not permitted|forbidden for this|insufficient/i;

export const isAuthFailureMessage = (message) => {
  const m = String(message ?? '');
  if (!m) return false;
  if (NOT_AUTH.test(m)) return false;
  return NO_IDENTITY.test(m) || BAD_TOKEN.test(m);
};

/**
 * The same question asked of an Apollo error payload.
 *
 * A real HTTP 401 still counts — an upload, or a proxy in front of the API,
 * can produce one — but a 403 network error does not, since that is how
 * environment gates and WAFs refuse a request that had a perfectly good token.
 */
export const isAuthFailure = ({ graphQLErrors, networkError } = {}) => {
  if ((graphQLErrors || []).some((e) => isAuthFailureMessage(e?.message))) return true;
  return networkError?.statusCode === 401;
};
